import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

import { api, ApiError } from "@/api/client";
import type { AuthInfo, QueueStatus } from "@/api/types";
import { ErrorState, LoadingState, useToast } from "@/components/ui";
import { connectServerEvents } from "@/lib/events";

import { AccountView } from "./features/account";
import { LoginView } from "./features/login";
import { QueueView } from "./features/queue";
import { SearchView } from "./features/search";
import { SettingsView } from "./features/settings";

export type Route =
  | { page: "queue"; status: QueueStatus }
  | { page: "search" }
  | { page: "settings" }
  | { page: "account" };

const QUEUE_STATUSES = new Set<QueueStatus>(["new", "review", "deep_read", "keep", "later", "archive", "todo"]);

function parseRoute(): Route {
  const raw = window.location.hash.replace(/^#\/?/, "");
  if (raw === "search") return { page: "search" };
  if (raw === "settings") return { page: "settings" };
  if (raw === "account") return { page: "account" };
  const status = raw.startsWith("queue/") ? raw.slice(6) : raw;
  return QUEUE_STATUSES.has(status as QueueStatus)
    ? { page: "queue", status: status as QueueStatus }
    : { page: "queue", status: "new" };
}

function navigate(path: string): void {
  window.location.hash = path;
}

const NAV_ICON: Partial<Record<Route["page"], string>> = {
  queue: "▤",
  search: "⌕",
  settings: "⚙",
};

export function AppNavigation({ auth, route, mobile = false }: { auth: AuthInfo; route: Route; mobile?: boolean }) {
  const current = (page: Route["page"]): "page" | undefined => route.page === page ? "page" : undefined;
  const content = (page: Route["page"], label: string) => mobile
    ? <><span class="nav-icon" aria-hidden="true">{NAV_ICON[page]}</span><span>{label}</span></>
    : label;
  return (
    <nav class={mobile ? "mobile-bottom-nav" : "app-nav"} aria-label={mobile ? "手机主导航" : "主导航"}>
      <button type="button" aria-current={route.page === "queue" ? "page" : undefined} onClick={() => navigate("queue/new")}>{content("queue", "论文")}</button>
      <button type="button" aria-current={current("search")} onClick={() => navigate("search")}>{content("search", "搜索")}</button>
      <button type="button" aria-current={current("settings")} onClick={() => navigate("settings")}>{content("settings", "设置")}</button>
    </nav>
  );
}

function pageTitle(route: Route): string {
  if (route.page === "queue") return "论文";
  if (route.page === "search") return "搜索";
  if (route.page === "settings") return "设置";
  if (route.page === "account") return "账号";
  return "论文";
}

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  useEffect(() => {
    const update = (): void => setRoute(parseRoute());
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);
  return route;
}

export function App({ queryClient }: { queryClient: QueryClient }) {
  const mode = useQuery({ queryKey: ["auth", "mode"], queryFn: api.authMode, staleTime: Infinity });
  const auth = useQuery<AuthInfo | null>({
    queryKey: ["auth", "check"],
    queryFn: async () => {
      try { return await api.authCheck(); }
      catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
  });

  if (mode.isPending || auth.isPending) return <LoadingState label="正在打开 DailyResearch…" />;
  if (mode.isError) return <ErrorState error={mode.error} retry={() => void mode.refetch()} />;
  if (auth.isError) return <ErrorState error={auth.error} retry={() => void auth.refetch()} />;
  if (!auth.data) return <LoginView mode={mode.data} onLogin={(info) => queryClient.setQueryData(["auth", "check"], info)} />;

  return <AuthenticatedApp auth={auth.data} queryClient={queryClient} />;
}

function AuthenticatedApp({ auth, queryClient }: { auth: AuthInfo; queryClient: QueryClient }) {
  const route = useRoute();
  const [eventsConnected, setEventsConnected] = useState(false);

  useEffect(() => {
    const connection = connectServerEvents(queryClient, setEventsConnected);
    return () => connection.close();
  }, [queryClient]);

  let content;
  if (route.page === "search") content = <SearchView />;
  else if (route.page === "settings") content = <SettingsView />;
  else if (route.page === "account") content = <AccountView />;
  else content = <QueueView status={route.status} user={auth.username || "local"} onNavigate={(status) => navigate(`queue/${status}`)} />;

  return <AppShell auth={auth} route={route} eventsConnected={eventsConnected}>{content}</AppShell>;
}

export function AppShell({ auth, route, eventsConnected, children }: { auth: AuthInfo; route: Route; eventsConnected: boolean; children: preact.ComponentChildren }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const mobileMenu = useRef<HTMLDetailsElement>(null);
  const accountMenu = useRef<HTMLDetailsElement>(null);
  const [trackedJobId, setTrackedJobId] = useState<number | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = window.localStorage.getItem("dr_theme");
    return saved === "dark" ? "dark" : "light";
  });
  const job = useQuery({
    queryKey: ["job", trackedJobId ?? "latest"],
    queryFn: () => trackedJobId === null ? api.jobStatus() : api.job(trackedJobId),
  });
  const sync = useQuery({ queryKey: ["sync"], queryFn: api.syncStatus, staleTime: 60_000 });
  const syncProblem = sync.data?.enabled === true && !sync.data.connected;
  const jobLabel = useMemo(() => {
    if (!job.data?.running) return "";
    const progress = job.data.progress;
    return `${progress?.phase ?? "任务"} ${progress?.done ?? 0}/${progress?.total ?? "—"}`;
  }, [job.data]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("dr_theme", theme);
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#002b36" : "#fdf6e3");
  }, [theme]);

  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      const menu = mobileMenu.current;
      if (menu?.open && event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
      const account = accountMenu.current;
      if (account?.open && event.target instanceof Node && !account.contains(event.target)) account.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && mobileMenu.current?.open) {
        mobileMenu.current.open = false;
        mobileMenu.current.querySelector<HTMLElement>("summary")?.focus();
      }
      if (event.key === "Escape" && accountMenu.current?.open) accountMenu.current.open = false;
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const runFetch = async (): Promise<void> => {
    try {
      const result = await api.runFetch();
      setTrackedJobId(result.job_id);
      await queryClient.invalidateQueries({ queryKey: ["job"] });
      toast("抓取任务已排队", "success");
    }
    catch (error) { toast(error instanceof Error ? error.message : "启动失败", "error"); }
  };
  const syncNow = async (): Promise<void> => {
    try {
      const result = await api.syncNow();
      setTrackedJobId(result.job_id);
      toast("同步任务已排队", "success");
      await queryClient.invalidateQueries({ queryKey: ["job"] });
    }
    catch (error) { toast(error instanceof Error ? error.message : "同步失败", "error"); }
  };
  const logout = async (): Promise<void> => {
    await api.logout();
    queryClient.clear();
    window.location.hash = "queue/new";
    window.location.reload();
  };
  const openAccountSettings = (): void => {
    window.localStorage.setItem("dr_settings_section", "account");
    navigate("settings");
    if (accountMenu.current) accountMenu.current.open = false;
  };
  const unpair = async (): Promise<void> => {
    if (!window.confirm("退出云端只会撤销此 Mac 的配对。本地论文、抓取组和操作记录都会保留。是否继续？")) return;
    try {
      await api.unpairSync();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["sync"] }),
        queryClient.invalidateQueries({ queryKey: ["settings"] }),
      ]);
      toast("此 Mac 已退出云端，本地数据保持不变", "success");
      openAccountSettings();
    } catch (error) { toast(error instanceof Error ? error.message : "退出云端失败", "error"); }
  };
  const closeMobileMenu = (): void => {
    if (mobileMenu.current) mobileMenu.current.open = false;
  };

  return (
    <div class="app-layout">
      <a class="skip-link" href="#main-content">跳到主要内容</a>
      <header class="app-header">
        <div class="brand" aria-label={jobLabel || (eventsConnected ? "实时更新已连接" : "实时更新重连中")}>
          <span class={`connection-dot${eventsConnected ? " connected" : ""}`} aria-hidden="true" />
          <span>{jobLabel || (eventsConnected ? "实时更新已连接" : "实时更新重连中")}</span>
        </div>
        <strong class="mobile-header-title">{pageTitle(route)}</strong>
        <AppNavigation auth={auth} route={route} />
        <div class="header-tools">
          <button type="button" class="btn btn-ghost header-icon" aria-label={job.data?.running ? "抓取中" : "抓取"} title={job.data?.running ? "抓取中…" : "抓取论文"} disabled={job.data?.running} onClick={() => void runFetch()} aria-busy={job.data?.running}>{job.data?.running ? "⏳" : "📥"}</button>
          {sync.data?.enabled && <button type="button" class="btn btn-ghost header-icon" aria-label={sync.data.connected ? "同步" : "同步异常"} title={sync.data.connected ? "立即同步" : "同步异常，点击重试"} onClick={() => void syncNow()}>{sync.data.connected ? "🔄" : "⚠️"}</button>}
          <button
            type="button"
            class="btn btn-ghost theme-toggle"
            aria-label="切换主题"
            title={theme === "dark" ? "切换到浅色" : "切换到深色"}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? "浅色" : "深色"}
          </button>
          <details class="account-menu" ref={accountMenu}>
            <summary class="btn btn-ghost">{auth.multiuser ? (auth.username || "账号") : sync.data?.enabled ? (auth.username || "云端已连接") : "离线使用 · 登录阿里云"}</summary>
            <div class="account-menu-panel">
              <div class="account-menu-status"><strong>{auth.multiuser ? (auth.username || "云端账号") : sync.data?.enabled ? "阿里云已连接" : "离线使用"}</strong><small>{sync.data?.last?.time ? `最近同步 ${sync.data.last.time}` : sync.data?.enabled ? "尚未完成首次同步" : "本地数据可正常使用"}</small>{Boolean(sync.data?.waiting_zotero) && <small>等待 Zotero 打开：{sync.data?.waiting_zotero} 篇</small>}</div>
              {sync.data?.enabled && <button type="button" class="btn btn-ghost" onClick={() => void syncNow()}>立即同步</button>}
              <button type="button" class="btn btn-ghost" onClick={auth.multiuser ? () => navigate("account") : openAccountSettings}>{auth.multiuser ? "账号与设备" : sync.data?.enabled ? "账号与同步设置" : "登录阿里云"}</button>
              {!auth.multiuser && sync.data?.enabled && <button type="button" class="btn btn-ghost danger-text" onClick={() => void unpair()}>退出云端</button>}
              {auth.multiuser && <button type="button" class="btn btn-ghost danger-text" onClick={() => void logout()}>退出登录</button>}
            </div>
          </details>
        </div>
        <details class="mobile-app-menu" ref={mobileMenu}>
          <summary aria-label="打开系统状态和更多操作">
            <span class={`connection-dot${syncProblem ? " error" : eventsConnected ? " connected" : ""}`} aria-hidden="true" />
            {jobLabel && <span class="mobile-job-label">{jobLabel}</span>}
            <span class="mobile-more-icon" aria-hidden="true">•••</span>
          </summary>
          <div class="mobile-app-menu-panel">
            <p class="mobile-system-status">
              <span class={`connection-dot${syncProblem ? " error" : eventsConnected ? " connected" : ""}`} aria-hidden="true" />
              <span><strong>{syncProblem ? "同步连接异常" : eventsConnected ? "实时更新已连接" : "实时更新重连中"}</strong>{jobLabel && <small>{jobLabel}</small>}</span>
            </p>
            <button type="button" class="btn btn-ghost" disabled={job.data?.running} onClick={() => { closeMobileMenu(); void runFetch(); }}>{job.data?.running ? "抓取中…" : "抓取论文"}</button>
            {sync.data?.enabled && <button type="button" class="btn btn-ghost" onClick={() => { closeMobileMenu(); void syncNow(); }}>{sync.data.connected ? "立即同步" : "同步异常，点击重试"}</button>}
            <button type="button" class="btn btn-ghost" onClick={() => { closeMobileMenu(); setTheme(theme === "dark" ? "light" : "dark"); }}>切换到{theme === "dark" ? "浅色" : "深色"}</button>
            <button type="button" class="btn btn-ghost" onClick={() => { closeMobileMenu(); auth.multiuser ? navigate("account") : openAccountSettings(); }}>{auth.multiuser ? "账号与设备" : sync.data?.enabled ? "账号与同步设置" : "离线使用 · 登录阿里云"}</button>
            {!auth.multiuser && sync.data?.enabled && <button type="button" class="btn btn-ghost danger-text" onClick={() => { closeMobileMenu(); void unpair(); }}>退出云端</button>}
            {auth.multiuser && <button type="button" class="btn btn-ghost danger-text" onClick={() => void logout()}>退出登录</button>}
          </div>
        </details>
      </header>
      <div id="main-content" tabIndex={-1}>{children}</div>
      <AppNavigation auth={auth} route={route} mobile />
    </div>
  );
}
